import { EXACT_HOST_MAP } from './constants';
import { Environment } from './types';

/**
 * Classify the current page URL into an Environment enum value.
 *
 * Optimized to branch by TLD first, reducing the number of string operations.
 *
 * Uses window.location.hostname and Atlassian URL patterns:
 * - *.jira-dev.com (Pug, Jdog, etc.)          -> staging
 * - *.atlassian.net                          -> prod
 * - Bifrost frontend.public.atl-paas.net     -> dev/staging/prod
 * - Bifrost CDN (FedRAMP)                    -> staging/prod
 * - *.dev|stg|.atlassian.com                 -> dev/staging/prod
 * - *.dev|staging|prod.atl-paas.net          -> dev/staging/prod
 * - *.staging|prod.atl-paas-us-gov-mod.net   -> staging/prod
 * - *.dev|staging|prod.services.atlassian.com-> dev/staging/prod
 * - localhost / 127.0.0.1                    -> dev
 */
export function getEnvironment(env?: string): Environment | string {
	if (env) {
		return env;
	}

	if (typeof window === 'undefined' || !window.location) {
		return Environment.Unknown;
	}

	const host = window.location.hostname.toLowerCase();

	// Fast path: exact hostname matches
	const exactMatch = EXACT_HOST_MAP[host];
	if (exactMatch) {
		return exactMatch;
	}

	// Extract TLD for efficient branching (find last dot position)
	const lastDot = host.lastIndexOf('.');
	if (lastDot === -1) {
		return Environment.Unknown;
	}

	const tld = host.slice(lastDot + 1); // 'com', 'net', etc.

	// Branch by TLD to reduce checks
	switch (tld) {
		case 'com':
			return classifyComDomain(host);
		case 'net':
			return classifyNetDomain(host);
		default:
			return Environment.Unknown;
	}
}

/**
 * Classify .com domains
 */
function classifyComDomain(host: string): Environment {
	// jira-dev.com (staging)
	if (host.endsWith('.jira-dev.com')) {
		return Environment.Staging;
	}

	// atlassian-us-gov-mod.com (FedRAMP)
	if (host.endsWith('.atlassian-us-gov-mod.com')) {
		// FedRAMP Bifrost CDN: check most specific first
		if (host.endsWith('.frontend.cdn.atlassian-us-gov-mod.com')) {
			return host.includes('.stg.') ? Environment.Staging : Environment.Prod;
		}

		if (host.endsWith('.stg.atlassian-us-gov-mod.com')) {
			return Environment.Staging;
		}

		return Environment.Prod;
	}

	// atlassian.com domains
	if (host.endsWith('.atlassian.com')) {
		// services.atlassian.com vanity domains (check most specific first)
		if (host.endsWith('.dev.services.atlassian.com')) {
			return Environment.Development;
		}

		if (host.endsWith('.staging.services.atlassian.com')) {
			return Environment.Staging;
		}

		if (host.endsWith('.services.atlassian.com')) {
			return Environment.Prod;
		}

		// Environment-specific subdomains
		if (host.endsWith('.dev.atlassian.com')) {
			return Environment.Development;
		}

		if (host.endsWith('.stg.atlassian.com')) {
			return Environment.Staging;
		}

		return Environment.Prod;
	}

	return Environment.Unknown;
}

/**
 * Classify .net domains
 */
function classifyNetDomain(host: string): Environment {
	// atlassian.net (prod tenants)
	if (host.endsWith('.atlassian.net')) {
		return Environment.Prod;
	}

	// atl-paas-us-gov-mod.net (FedRAMP Micros)
	if (host.endsWith('.atl-paas-us-gov-mod.net')) {
		if (host.includes('.staging.')) {
			return Environment.Staging;
		}

		if (host.includes('.prod.')) {
			return Environment.Prod;
		}

		return Environment.Unknown;
	}

	// atl-paas.net (commercial Micros and Bifrost)
	if (host.endsWith('.atl-paas.net')) {
		// Bifrost commercial: check most specific first
		if (host.endsWith('.frontend.public.atl-paas.net')) {
			if (host.includes('.ddev.') || host.includes('.dev-west2.')) {
				return Environment.Development;
			}

			if (host.includes('.stg-east.')) {
				return Environment.Staging;
			}

			if (host.includes('.prod-east.')) {
				return Environment.Prod;
			}

			return Environment.Development;
		}

		// Micros commercial
		if (host.includes('.dev.') || host.includes('.platdev.')) {
			return Environment.Development;
		}

		if (host.includes('.staging.')) {
			return Environment.Staging;
		}

		if (host.includes('.prod.')) {
			return Environment.Prod;
		}
	}

	return Environment.Unknown;
}
